import type { MorphemeGlyphTileGraphemeMorphemeSlice } from "@-/dev.improve.haniverse.lib.morpheme.entities";
import { useMorphemeListStagesQuery } from "@-/dev.improve.haniverse.lib.morpheme.stage";
import { keepPreviousData, useQuery } from "@-/com.npmjs.tanstack__react-query";
import { useEffect, useMemo } from "react";
import { useMorphemeGlyphTileGraphemeMorphemeSlices } from "#use-morpheme-glyph-tile-grapheme-morpheme-slice";
import type { MorphemeGlyphTileStage } from "#morpheme-glyph-tile-ui";

/** Stage a tile renders with before the batched stage query has resolved. */
const PLACEHOLDER_STAGE: MorphemeGlyphTileStage = "unadded";

export type BatchedGraphemeTileData = {
  /**
   * `index` is the tile's position in the grid's list, for a grid whose repeated grapheme stands
   * for different cards (a pronunciation level's 干 gan4 and 干 gan1): each tile's slice can then
   * colour the glyph by its own card's reading.
   */
  getSlice: (grapheme: string, index?: number) => MorphemeGlyphTileGraphemeMorphemeSlice;
  /** `index` as for `getSlice`: each tile's stage can follow its own card. */
  getStage: (grapheme: string, index?: number) => MorphemeGlyphTileStage;
};

export type BatchedGraphemeTileDataOptions = {
  /**
   * Gates the stage read while this grid's subtree is inactive. Pass a FUNCTION (not a boolean) for
   * a frozen-subtree gate: React Native Screens can freeze a preserved tab before its blur render
   * commits, so a rendered boolean goes stale while an imperative read stays current. A write while
   * the grid is gated only marks its stages stale; nothing is read until it is enabled again.
   *
   * Defaults to `true`: a grid that isn't inside a preserved/frozen navigator (a lesson surface, a
   * panel section) has nothing to gate on and must never disable itself.
   */
  enabled?: boolean | (() => boolean);
  /**
   * Monotonic counter that triggers exactly one catch-up refetch when it increases — pair it with
   * `enabled` so a gated grid refreshes on re-entry after the writes it ignored while inactive.
   * Omit it when `enabled` is left at its default.
   */
  refetchVersion?: number;
};

/**
 * The two batched reads a grid of grapheme tiles needs, lifted ONCE for the whole set.
 *
 * A tile only renders query-free when it is handed BOTH a `stage` and a
 * `morphemeGlyphTileSlice` (see `GraphemeMorphemeGlyphTileUi`) — supplying just one still
 * drops it onto the self-resolving branch, which fires a per-tile stage `useQuery` + change
 * subscription + pinyin read. Pairing the two lifts here makes the fast path the only thing a grid
 * has to opt into.
 *
 * Why this matters: the per-tile stage query is keyed by its grapheme ARRAY, so `["一"]` and `["二"]`
 * are distinct cache entries — N tiles meant N stage queries, each with its own change registration,
 * each re-reading BOTH the meaning and pronunciation overlays on every committed actor write. On a
 * lesson level surface (8–16 tiles, and previous lesson screens stay stacked and subscribed) a
 * single card rating fanned that into dozens of serialized actor reads whose wall time was almost
 * entirely queue wait.
 *
 * Both resolvers return neutral placeholders while the reads are pending, so a tile stays on the
 * prop-resolved branch for its whole lifetime and can never fall back to per-tile queries during the
 * pending window.
 *
 * `graphemes` MUST be referentially stable (memoize it): the batched slice read keys its work on the
 * array's identity, and the stage query re-derives its key from the array on every render.
 */
export function useBatchedGraphemeTileData(
  graphemes: readonly string[],
  { enabled = true, refetchVersion }: BatchedGraphemeTileDataOptions = {},
): BatchedGraphemeTileData {
  const sliceByGrapheme = useMorphemeGlyphTileGraphemeMorphemeSlices(graphemes);
  const { data: stageByGrapheme, refetch: refetchStages } = useQuery({
    ...useMorphemeListStagesQuery(graphemes),
    enabled,
    placeholderData: keepPreviousData,
    refetchOnReconnect: enabled,
    refetchOnWindowFocus: enabled,
  });

  useEffect(() => {
    if (refetchVersion !== undefined && refetchVersion > 0) {
      // Writes are deliberately ignored while a gated subtree is inactive, so re-entering it needs
      // exactly one catch-up read. A monotonic version (rather than a boolean) survives the frozen
      // false → true transition being collapsed into a single render by React Native Screens.
      void refetchStages({ cancelRefetch: false });
    }
  }, [refetchStages, refetchVersion]);

  return useMemo<BatchedGraphemeTileData>(
    () => ({
      getSlice: (grapheme) =>
        sliceByGrapheme.get(grapheme) ?? {
          additionalPinyin: [],
          grapheme,
          morphemeType: "grapheme",
          primaryPinyin: null,
        },
      getStage: (grapheme) => stageByGrapheme?.[grapheme] ?? PLACEHOLDER_STAGE,
    }),
    [sliceByGrapheme, stageByGrapheme],
  );
}
